import React, { useCallback, useEffect, useRef, useState } from "react";
import Paginate from "../../CommonComponents/Pagination/Paginate";
import { apiJsonRequest } from "../../../utils/apiRequest";
import LoadingScreen from "../../Elements/LoadingScreen/LoadingScreen";
import LinkCard from "./LinkCard";
import { Container } from "react-bootstrap";
import LaserLinkModal from "../../Elements/LaserLink/LaserLinkModal";
import { networkService } from "../../config/axios";
import CommonModal from "../../Modals/CommonModal";
import Button from "../../Elements/Button";
import LinksPageFilter from "./LinksPageFilter";
import customAPIRequest from "../../../utils/customAPIRequest";
import { useLocation } from "react-router-dom";

const LinkPage = ({ time_zone_list }) => {
  const [linkListData, setLinkListData] = useState({ loading: true, data: [] });
  const [selectedKDFLink, setSelectedKDFLink] = useState(null);
  const [getDeleteLaserLinkItem, setDeleteLaserLinkItem] = useState(null);
  const [initialReceiver, setInitialReceiver] = useState(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [quickBarNode, setQuickBarNode] = useState(null);
  const itemsPerPage = 10;
  const { pathname, search } = useLocation();
  const lastFiltersRef = useRef({});
  const fetchSeqRef = useRef(0);
  const initialLoadRef = useRef(false);
  const currentLocationKeyRef = useRef(`${pathname}${search || ""}`);
  
  // Global pending request tracker for component-level deduplication
  // This works alongside apiJsonRequest's deduplication to prevent duplicate calls
  if (typeof window !== 'undefined' && !window.__LINK_PAGE_PENDING_REQUEST__) {
    window.__LINK_PAGE_PENDING_REQUEST__ = null;
  }

  // Reset initialLoadRef if location has changed (pathname/search)
  useEffect(() => {
    const nextKey = `${pathname}${search || ""}`;
    if (currentLocationKeyRef.current !== nextKey) {
      currentLocationKeyRef.current = nextKey;
      initialLoadRef.current = false;
      lastFiltersRef.current = {};
      if (typeof window !== 'undefined') {
        window.__LINK_PAGE_PENDING_REQUEST__ = null;
      }
    }
  }, [pathname, search]);

  const updateLaserLinksData = useCallback(async (page = 1, filters = lastFiltersRef.current || {}, { silent = false } = {}) => {
    const seq = ++fetchSeqRef.current;

    let getValue = {
      page: page,
      per_page: itemsPerPage,
      is_filter: true,
      ...filters,
    };
    if (pathname?.includes('links/doc')) {
      getValue['doc_name'] = window?.objDoc?.title;
    } else if (pathname.includes('links/tl')) {
      let path = pathname;
      let token = path?.split('/').pop();
      getValue['token'] = token;
    }
    
    // Create a stable payload key for deduplication
    const payloadKey = JSON.stringify(getValue);
    
    // Clear pending request if payload differs (e.g., pagination/filter change)
    if (typeof window !== 'undefined' && window.__LINK_PAGE_PENDING_REQUEST__) {
      const existing = window.__LINK_PAGE_PENDING_REQUEST__;
      if (existing.payloadKey !== payloadKey) {
        window.__LINK_PAGE_PENDING_REQUEST__ = null;
      }
    }
    
    // Check if there's already a pending request with the same payload (component-level deduplication)
    // This works alongside apiJsonRequest's deduplication to prevent duplicate calls from StrictMode
    const pendingRequest = window.__LINK_PAGE_PENDING_REQUEST__;
    if (pendingRequest && pendingRequest.payloadKey === payloadKey && pendingRequest.promise) {
      // Update loading state and wait for the existing promise
      if (!silent) {
        setLinkListData((prev) => ({ ...prev, loading: true }));
      }

      try {
        const apiResponse = await pendingRequest.promise;
        const data = apiResponse?.data || apiResponse;
        if (seq !== fetchSeqRef.current) return;

        if (data) {
          setLinkListData({
            ...data,
            loading: false,
            page: page,
            per_page: itemsPerPage,
          });
        }
      } catch (error) {
        if (seq !== fetchSeqRef.current) return;
        setLinkListData((prev) => ({
          ...prev,
          loading: false,
          error: error?.error?.message || error?.message || "Failed to fetch links"
        }));
      }

      return; // Exit early, we're using the shared promise
    }

    // No pending request, create a new one
    if (!silent) {
      setLinkListData((prev) => ({ ...prev, loading: true }));
    }

    // Create the request promise and store it globally for deduplication
    const requestPromise = apiJsonRequest("links/search.json", "POST", getValue);
    
    // Store the pending request globally so other calls can reuse it
    if (typeof window !== 'undefined') {
      window.__LINK_PAGE_PENDING_REQUEST__ = {
        payloadKey: payloadKey,
        promise: requestPromise,
        timestamp: Date.now()
      };
    }
    
    try {
      let { data } = await requestPromise;
      if (seq !== fetchSeqRef.current) return;
      setLinkListData({
        ...data,
        loading: false,
        page: page,
        per_page: itemsPerPage,
      });
    } catch (error) {
      if (seq !== fetchSeqRef.current) return;
      setLinkListData((prev) => ({
        ...prev,
        loading: false,
        error: error?.error?.message || error?.message || "Failed to fetch links"
      }));
    } finally {
      // Clear the pending request after a short delay to allow StrictMode remounts to complete
      setTimeout(() => {
        if (typeof window !== 'undefined' && window.__LINK_PAGE_PENDING_REQUEST__) {
          const pending = window.__LINK_PAGE_PENDING_REQUEST__;
          const timeSinceRequest = Date.now() - pending.timestamp;
          // Only clear if it's been more than 100ms (enough time for StrictMode remounts)
          if (timeSinceRequest > 100) {
            window.__LINK_PAGE_PENDING_REQUEST__ = null;
          }
        }
      }, 150);
    }
  }, [pathname, itemsPerPage]);

  const reloadCurrentPage = (page = linkListData?.page || 1) =>
    updateLaserLinksData(page, lastFiltersRef.current, { silent: true });

  const refreshLink = async (id) => {
    const { data } = await apiJsonRequest("links/search.json", "POST", {
      page: 1,
      per_page: 1,
      is_filter: true,
      tl_ids: [id],
    });
    const fresh = data?.tracking_links?.[0];
    if (!fresh) return;
    setLinkListData((prev) => ({
      ...prev,
      tracking_links: (prev?.tracking_links || []).map((link) =>
        link.id === fresh.id ? fresh : link
      ),
    }));
  };

  const handleCopyLaserLinkEvent = async (listingID) => {
    let { data } = await networkService(
      `/links/clone/${listingID}.json`,
      "GET"
    );
    if (data.msg == "ok") {
      reloadCurrentPage();
    }
  };

  useEffect(() => {
    // Prevent duplicate initial load calls within the same mount
    // Allow StrictMode remounts to proceed - component-level deduplication will handle them
    if (initialLoadRef.current) {
      return;
    }
    initialLoadRef.current = true;
    
    const params = new URLSearchParams(search || "");
    const receiverId = params.get("receiver_id");
    if (receiverId) {
      lastFiltersRef.current = { receiver_id: receiverId };
      updateLaserLinksData(1, lastFiltersRef.current);
      networkService(`receivers/${receiverId}.json`).then(({ data }) => {
        if (data) {
          const name = [data.first_name, data.last_name].filter(Boolean).join(' ') || data.email;
          setInitialReceiver({ id: data.id, name_email: `${name} (${data.email})`, tl_ids: data.tl_ids });
        }
      }).catch((err) => console.error(err));
    } else {
      setInitialReceiver(null);
      updateLaserLinksData();
    }
  }, [updateLaserLinksData, search]);

  const handleDeleteLaserLinkItemEvent = async () => {
    if (!getDeleteLaserLinkItem) return;
    let { status } = await networkService(
      `/links/${getDeleteLaserLinkItem}.json`,
      "DELETE"
    );
    if (status == 204) {
      const page = linkListData?.page || 1;
      const lastOnPage = (linkListData?.tracking_links?.length || 0) <= 1;
      reloadCurrentPage(lastOnPage && page > 1 ? page - 1 : page);
      setDeleteLaserLinkItem(null);
    }
  };

  const handleUpdateLaserLinkData = useCallback(async (docData, dataPayload, options = {}) => {
    const { skipRefresh = false } = options;
    let { status } = await networkService(
      `/links/${docData.id}.json`,
      "PATCH",
      {
        id: docData?.id,
        name: docData?.name,
        doc_id: docData?.doc.id,
        tl: {},
        tag_ids: [...dataPayload],
      }
    );

    if (status == 200 && !skipRefresh) {
      updateLaserLinksData();
    }
    return status;
  }, [updateLaserLinksData]);

  const handleFiltersApplyEvent = async (payload, filterCount) => {
    const normalizedPayload =
      payload && typeof payload === "object" && Object.keys(payload).length > 0
        ? payload
        : {};
    lastFiltersRef.current = normalizedPayload;
    setActiveFilterCount(filterCount ?? Object.keys(normalizedPayload).length);
    await updateLaserLinksData(1, normalizedPayload);
  };

  return (
    <>
      <LaserLinkModal
        laserItem={selectedKDFLink}
        time_zone_list={time_zone_list}
        updateSelectedKDFLink={setSelectedKDFLink}
        triggerCancelProcess={(val) => {
          setSelectedKDFLink(val);
        }}
        triggerUpdateList={(id) => (typeof id === "number" ? refreshLink(id) : reloadCurrentPage())}
      />
      <CommonModal
        title={"Delete LaserLINK"}
        modalStatus={getDeleteLaserLinkItem != null}
        changeActiveStatus={() => setDeleteLaserLinkItem(null)}
        centered
        classList={"upload-kdf-modal-wrapper alert-modal"}
        footer={
          <div className="d-flex m-auto">
            <Button
              text={"Cancel"}
              classList={"alert delete-btn"}
              addonClass={"my-2 mx-2"}
              onClick={() => setDeleteLaserLinkItem(null)}
              variant="outline"
            />
            <Button
              text={"Delete"}
              classList={"success delete-btn"}
              addonClass={"my-2 mx-2 common-modal-yes-button"}
              onClick={handleDeleteLaserLinkItemEvent}
            />
          </div>
        }
      >
        <p style={{ fontSize: "14px" }}>
          Are you sure you want to delete this LaserLINK? This action cannot be
          undone.
        </p>
      </CommonModal>
      <div className={`header-content-main links-filter-header${isFiltersOpen ? " filters-open" : ""}`}>
        <div className="doc-filter compact-filters">
          <div className="filter-toggle" aria-expanded={isFiltersOpen}>
            <button
              type="button"
              className="filter-toggle-label"
              onClick={() => setIsFiltersOpen((prev) => !prev)}
            >
              <span className="filter-toggle-left">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1.5 2.5H14.5L9.5 8.5V13L6.5 14.5V8.5L1.5 2.5Z" stroke="#6b7280" strokeWidth="1.3" strokeLinejoin="round" strokeLinecap="round" />
                </svg>
                <h4 className="filter-title">Filters</h4>
                {activeFilterCount > 0 && (
                  <span className="filter-count-badge">{activeFilterCount}</span>
                )}
              </span>
            </button>
            {!isFiltersOpen && (
              <div className="filters-quick-bar" ref={setQuickBarNode} />
            )}
            <button
              type="button"
              className="filter-toggle-chevron-btn"
              aria-label={isFiltersOpen ? "Collapse filters" : "Expand filters"}
              onClick={() => setIsFiltersOpen((prev) => !prev)}
            >
              <svg
                className={`filter-chevron ${isFiltersOpen ? "open" : ""}`}
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M4 6L8 10L12 6" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <div className={`filter-body ${isFiltersOpen ? "open" : ""}`}>
            <div className="filter-body-inner">
              <LinksPageFilter
                triggerFiltersApplyEvent={handleFiltersApplyEvent}
                kdf={{ 'value': window?.objDoc?.title || '' }}
                initialReceiver={initialReceiver}
                isOpen={isFiltersOpen}
                quickBarTarget={quickBarNode}
              />
            </div>
          </div>
        </div>
      </div>
      {linkListData.loading ? (
        <LoadingScreen />
      ) : linkListData?.tracking_links?.length > 0 ? (
        <div style={{ maxWidth: "960px", margin: "20px auto" }}>
          {linkListData?.tracking_links?.map((linkData, linkDataIndex) => (
            <LinkCard
              data={linkData}
              key={linkData?.id || linkDataIndex}
              trigerCopyLaserLinkEvent={handleCopyLaserLinkEvent}
              triggerSettingsLaserLinkEvent={setSelectedKDFLink}
              triggerDeleteLaserLinkEvent={setDeleteLaserLinkItem}
              updateLinkData={handleUpdateLaserLinkData}
              triggerUdateRecord={updateLaserLinksData}
            />
          ))}
          {linkListData?.cnt > linkListData?.per_page && (
            <Paginate
              currentPage={linkListData?.page}
              totalPages={Math.ceil(linkListData?.cnt / linkListData?.per_page)}
              onPageChange={(page) => updateLaserLinksData(page)}
              totalData={linkListData?.cnt}
              itemTitle={linkListData?.cnt > 1 ? "LaserLINX" : "LaserLINK"}
              pageSize={linkListData?.per_page}
              currentCount={linkListData?.tracking_links?.length}
            />
          )}
        </div>
      ) : (
        <div className="listing-empty-state">
          Sorry, We tried our best but could NOT find a LaserLINK matching the
          filter you used. Please reset the filter and try again.
        </div>
      )}
    </>
  );
};

export default LinkPage;
